import {
  autoFillRosterToMinimums,
  buildFranchiseDepthChart,
  mergeFranchiseDepthChart,
  orderFranchiseRoster,
  playersAtPosition,
  resolveDepthChartPlayer,
  type FranchiseDepthChartConfig,
  type FranchiseDepthChartInput,
  type FranchisePlayer,
} from '#shared';

function clearTeamRosterCaches(team: any): void {
  delete team._resolvedDepthChartConfig;
  delete team._rosterGroups;
  delete team._defensiveGroups;
  delete team._returner;
  delete team._returners;
  delete team._playersByPosition;
  delete team._speedProfile;
}

export function rosterList(team: any): FranchisePlayer[] {
  return (team?.roster || team?.players || []) as FranchisePlayer[];
}

function distinctRosterPlayers(players: FranchisePlayer[]): FranchisePlayer[] {
  const seen = new Set<string>();
  return players.filter((player) => {
    const playerId = String(player?.id || '');
    if (!playerId || seen.has(playerId)) {
      return false;
    }
    seen.add(playerId);
    return true;
  });
}

export function teamDepthChartConfig(team: any): FranchiseDepthChartConfig {
  if (!team?._resolvedDepthChartConfig) {
    team._resolvedDepthChartConfig = mergeFranchiseDepthChart(
      rosterList(team),
      team?.depthChartConfig || null,
    );
  }

  return team._resolvedDepthChartConfig as FranchiseDepthChartConfig;
}

export function teamPlayersAtPosition(team: any, position: string, count?: number): FranchisePlayer[] {
  const players = playersAtPosition(rosterList(team), position, teamDepthChartConfig(team));
  return typeof count === 'number' ? players.slice(0, count) : players;
}

export function teamOrderedRoster(team: any): FranchisePlayer[] {
  return orderFranchiseRoster(rosterList(team), teamDepthChartConfig(team));
}

export function teamReturnerPlayer(team: any, kind: 'kick' | 'punt' = 'kick'): FranchisePlayer | null {
  const roster = rosterList(team);
  const config = teamDepthChartConfig(team);
  const depthChart = buildFranchiseDepthChart(roster, config);
  const configuredPlayerId = kind === 'punt' ? config.roles.puntReturner : config.roles.kickReturner;
  const resolvedReturner = kind === 'punt' ? depthChart.puntReturner : depthChart.kickReturner;

  return resolveDepthChartPlayer(roster, configuredPlayerId)
    || resolvedReturner
    || depthChart.returner
    || roster[0]
    || null;
}

export function applyTeamDepthChart(
  team: any,
  depthChartInput?: FranchiseDepthChartInput | FranchiseDepthChartConfig | null,
): any {
  // Sim teams always field the franchise minimum at every position: rosters
  // short of the minimums (e.g. NFT-only depth charts) are padded with
  // deterministic auto-fill placeholders. Idempotent, so re-applying a depth
  // chart mid-game (backfield swaps, branding refreshes) adds nothing.
  const roster = autoFillRosterToMinimums(
    distinctRosterPlayers(rosterList(team)),
    team?.simulationId || team?.id || '',
  );
  const depthChartConfig = mergeFranchiseDepthChart(roster, depthChartInput || team?.depthChartConfig || null);
  const orderedRoster = orderFranchiseRoster(roster, depthChartConfig);
  const depthChart = buildFranchiseDepthChart(orderedRoster, depthChartConfig);
  const nextTeam = {
    ...team,
    roster: orderedRoster,
    players: orderedRoster,
    depthChartConfig,
    depthChart,
  };

  clearTeamRosterCaches(nextTeam);
  return nextTeam;
}
